import { useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { PiPlusBold, PiTrashBold, PiTreeStructureBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import CostEstimateBomExpandDialog from '#/modules/crmMgmt/costEstimate/components/CostEstimateBomExpandDialog';
import {
  mergeItemsIntoLines,
  suggestedUnitPrice,
} from '#/modules/crmMgmt/costEstimate/helper/costEstimateLines';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import BomPickerDialog from '#/modules/momMgmt/settings/bom/components/BomPickerDialog';
import { toast } from '#/utils/toast';

const CELL_INPUT_CLASS = 'h-9 min-h-9 w-full';

/**
 * 成本試算明細編輯：手動加料（ACTIVE 一般物料）、從 BOM 展開、改數量／單價／備註、上下移與移除。
 * 同料只累加數量並保留原單價與位置；單價留空時由後端帶物料建議價。
 */
const CostEstimateLinesEditor = ({ control, register, errors, getValues }) => {
  const { fields, replace, remove, move } = useFieldArray({
    control,
    name: 'lines',
    keyName: 'fieldKey',
  });
  const [mtrPickerOpen, setMtrPickerOpen] = useState(false);
  const [bomPickerOpen, setBomPickerOpen] = useState(false);
  const [bomId, setBomId] = useState(null);

  function mergeItems(items, sourceLabel) {
    const current = getValues('lines') ?? [];
    const merged = mergeItemsIntoLines(current, items);
    replace(merged);
    const added = merged.length - current.length;
    const accumulated = items.length - added;
    toast.success(`${sourceLabel}：新增 ${added} 列${accumulated > 0 ? `、${accumulated} 列同料累加數量` : ''}`);
  }

  function handleMtrPicked(rows) {
    const items = (rows ?? []).map((row) => ({
      mtrId: row.optionMtrId,
      mtrNo: row.mtrNo,
      name: row.name,
      baseUnitName: row.baseUnitName ?? '',
      qty: '1',
      unitPrice: suggestedUnitPrice(row.baseUnitPrice),
    }));
    if (items.length) {
      mergeItems(items, '加入物料');
    }
  }

  function handleBomPicked(row) {
    if (row?.id) {
      setBomId(String(row.id));
    }
  }

  return (
    <section className="@container/form-section pb-5">
      <div className="form-section__header flex-wrap gap-2">
        <h2 className="form-block__title mb-0!">試算明細</h2>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => setMtrPickerOpen(true)}>
            <PiPlusBold className="size-3.5" />
            加入物料
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setBomPickerOpen(true)}>
            <PiTreeStructureBold className="size-3.5" />
            從 BOM 加入
          </Button>
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        固定 TWD；數量與單價依物料基礎單位，最多四位小數。單價留空會帶物料建議價（無建議價為 0）。同一物料只會有一列，再次加入只累加數量。
      </p>

      <div className="mt-3 overflow-x-auto rounded border border-gray-400/65">
        <table className="w-full min-w-[60rem] text-left text-sm">
          <thead className="bg-gray-100 text-xs text-gray-600">
            <tr>
              <th className="w-10 px-2 py-2 text-center font-medium">#</th>
              <th className="px-2 py-2 font-medium">物料</th>
              <th className="w-20 px-2 py-2 font-medium">單位</th>
              <th className="w-32 px-2 py-2 font-medium">數量</th>
              <th className="w-36 px-2 py-2 font-medium">單價</th>
              <th className="w-56 px-2 py-2 font-medium">備註</th>
              <th className="w-32 px-2 py-2 text-center font-medium">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dashed divide-gray-400/65">
            {fields.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-sm text-muted-foreground">
                  尚無明細，可先儲存零明細試算。
                </td>
              </tr>
            ) : fields.map((field, index) => {
              const qtyError = getNestedError(errors, `lines.${index}.qty`);
              const priceError = getNestedError(errors, `lines.${index}.unitPrice`);
              return (
                <tr key={field.fieldKey} className="align-top">
                  <td className="px-2 py-2 text-center tabular-nums text-gray-600">{index + 1}</td>
                  <td className="px-2 py-2">
                    <p className="font-medium text-gray-900">{field.mtrNo || '-'}</p>
                    <p className="text-xs text-gray-600">{field.name || '-'}</p>
                    {field.lineId ? null : (
                      <p className="text-xs text-primary-600">尚未儲存</p>
                    )}
                  </td>
                  <td className="px-2 py-2 align-middle text-gray-700">{field.baseUnitName || '-'}</td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      aria-label={`數量 ${index + 1}`}
                      aria-invalid={qtyError ? true : undefined}
                      title={qtyError?.message || undefined}
                      className={cn(CELL_INPUT_CLASS, qtyError && 'form_error')}
                      {...register(`lines.${index}.qty`)}
                    />
                    {qtyError ? <p className="mt-0.5 text-xs text-destructive">{qtyError.message}</p> : null}
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      placeholder="建議價"
                      aria-label={`單價 ${index + 1}`}
                      aria-invalid={priceError ? true : undefined}
                      title={priceError?.message || undefined}
                      className={cn(CELL_INPUT_CLASS, priceError && 'form_error')}
                      {...register(`lines.${index}.unitPrice`)}
                    />
                    {priceError ? <p className="mt-0.5 text-xs text-destructive">{priceError.message}</p> : null}
                  </td>
                  <td className="px-2 py-2">
                    <Input
                      type="text"
                      aria-label={`備註 ${index + 1}`}
                      className={CELL_INPUT_CLASS}
                      {...register(`lines.${index}.remark`)}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`上移第 ${index + 1} 列`}
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`下移第 ${index + 1} 列`}
                        disabled={index === fields.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                        aria-label={`移除第 ${index + 1} 列`}
                        onClick={() => remove(index)}
                      >
                        <PiTrashBold className="size-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {mtrPickerOpen ? (
        <MtrPickerDialog
          open
          onOpenChange={setMtrPickerOpen}
          onConfirm={handleMtrPicked}
          title="加入物料"
          description="只列出啟用中的一般物料；可多選，已在明細的物料會累加數量 1。"
        />
      ) : null}
      {bomPickerOpen ? (
        <BomPickerDialog
          open
          onOpenChange={setBomPickerOpen}
          onConfirm={handleBomPicked}
          title="選擇 BOM"
          description="預設只列啟用中的 BOM，勾「含停用」可找停用 BOM；選定後確認物料與替代料。"
          allowInactive
        />
      ) : null}
      {bomId ? (
        <CostEstimateBomExpandDialog
          key={bomId}
          open
          bomId={bomId}
          onOpenChange={(open) => { if (!open) setBomId(null); }}
          onConfirm={(items, bom) => mergeItems(items, `BOM ${bom?.code ?? ''}`)}
        />
      ) : null}
    </section>
  );
};

CostEstimateLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
};

export default CostEstimateLinesEditor;
